import { Component, ViewChild, AfterViewInit, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ReusableGoogleMapComponent } from '@app/components/reusable-google-map/reusable-google-map.component';
import { SendNotificationDialogComponent } from '@app/components/send-notification-dialog/send-notification-dialog.component';
import { LocateDriverService } from '@app/services/locate-driver.service';
import { ToastService } from '@app/services/toast.service';
import { ShareddtaService } from '@app/services/shareddta.service';
import { Subject } from 'rxjs';
import { Router } from '@angular/router';
import { BreadcrumbService } from '@app/services/breadcrumb.service';
interface Driver {
  id: number;
  name: string;
  vehicle: string;
  status: 'online' | 'offline' | 'pending';
  lastActive: string;
  location: string;
  lat?: number;
  lng?: number;
  first_name: string;
  last_name: string;
}

interface DriverCounts {
  all: number;
  online: number;
  offline: number;
  pending: number;
}
@Component({
  selector: 'app-locate-driver',
  templateUrl: './locate-driver.component.html',
  styleUrl: './locate-driver.component.scss'
})
export class LocateDriverComponent implements OnInit, AfterViewInit {
  private destroy$ = new Subject<void>();
  mapKey = Date.now();
  // Search
  searchQuery: string = '';
  showClearButton: boolean = false;

  // Filters
  activeFilter: 'all' | 'online' | 'offline' | 'pending' = 'all';

  // Drivers
  drivers: Driver[] = [];
  filteredDrivers: Driver[] = [];

  // Selected driver
  selectedDriverId: number | null = null;

  // Popup
  showPopup: boolean = false;
  popupDriver: Driver | null = null;
  popupPosition = { top: '30%', left: '50%' };

  // Counts
  driverCounts: DriverCounts = {
    all: 0,
    online: 0,
    offline: 0,
    pending: 0
  };

  isLoading: boolean = true;

  constructor(private dialog: MatDialog, private locateDriverService: LocateDriverService, private toast: ToastService, private sharedData: ShareddtaService,public router: Router, private breadcrumbService: BreadcrumbService) { }

  ngAfterViewInit(): void {
    this.sharedData.signalPageReady();
  }

  ngOnInit(): void {
    const isReceivingSide = this.router.url.includes('/receiving-admin');
    this.breadcrumbService.set([
      { 
        label: 'Dashboard', 
        icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6', 
        action: () => this.router.navigate([isReceivingSide ? '/receiving-admin/dashboard' : '/admin/dashboard']) 
      },
      { label: 'Locate Drivers' }
    ]);
    // 🔹 Removed redundant window.location.reload() to stabilize session state
    this.isLoading = true;
    this.loadDrivers();
    this.activeFilter = localStorage.getItem('locateDriverFilter') as 'all' | 'online' | 'offline' | 'pending' || 'all';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load drivers from service
   */
  loadDrivers(): void {
    // this.locateDriversService.getDrivers()
    //   .pipe(takeUntil(this.destroy$))
    //   .subscribe(
    //     (drivers) => {
    //       this.drivers = drivers;
    //       this.updateCounts();
    //       this.applyFilters();
    //     },
    //     (error) => {
    //       console.error('Error loading drivers:', error);
    //     }
    //   );
  }

  /**
   * Update driver counts
   */
  updateDriverCounts(drivers: any[]) {
    this.driverCounts.all = drivers.length;
    this.driverCounts.online = drivers.filter(d => d.is_online === true).length;
    this.driverCounts.pending = drivers.filter(d => !d.drivertime?.length).length;
    this.driverCounts.offline = drivers.filter(d =>
      d.is_online === false
    ).length;
    this.isLoading = false;
  }

  /**
   * Search drivers by name
   */
  onSearchInput(query: string): void {
    this.searchQuery = query;
    this.showClearButton = query.trim().length > 0;
    this.applyFilters();
  }

  /**
   * Clear search
   */
  clearSearch(): void {
    this.searchQuery = '';
    this.showClearButton = false;
    this.applyFilters();
  }

  /**
   * Filter drivers by status
   */
  filterDrivers(filter: 'all' | 'online' | 'offline' | 'pending'): void {
    this.activeFilter = filter;
    localStorage.setItem('locateDriverFilter', filter);
    this.applyFilters();
    
    if (filter === 'pending') {
      this.toast.show('warning', 'Pending drivers have not logged in yet, so they have no location to display on the map.');
    }
  }

  /**
   * Apply all filters (search + status)
   */
  applyFilters(): void {
    let filtered = [...this.drivers];

    // Apply status filter
    if (this.activeFilter !== 'all') {
      filtered = filtered.filter(d => d.status === this.activeFilter);
    }

    // Apply search filter
    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(d => d.name.toLowerCase().includes(query));
    }

    this.filteredDrivers = filtered;
  }

  /**
   * Select driver card
   */
  selectDriver(driver: Driver, event?: Event): void {
    if (driver.status === 'pending') {
      this.toast.show('warning', 'Location unavailable: Driver has not logged in yet.');
      return;
    }

    this.selectedDriverId = driver.id;

    // Show popup on map
    if (event) {
      const target = event.currentTarget as HTMLElement;
      this.showDriverPopup(driver, target);
    }

    // Highlight marker
    this.highlightMarker(driver.id);
  }

  /**
   * Show driver popup
   */
  showDriverPopup(driver: Driver, element?: HTMLElement): void {
    this.popupDriver = driver;

    // Calculate position if element provided
    if (element) {
      const rect = element.getBoundingClientRect();
      this.popupPosition = {
        top: `${rect.top - 170}px`,
        left: `${rect.left - 120}px`
      };
    }

    this.showPopup = true;

    // Auto-hide after 6 seconds
    setTimeout(() => {
      this.hidePopup();
    }, 6000);
  }

  /**
   * Hide popup
   */
  hidePopup(): void {
    this.showPopup = false;
  }

  /**
   * Highlight marker on map
   */
  highlightMarker(driverId: number): void {
    // Add logic to scale up and highlight marker
    setTimeout(() => {
      // Reset after 3 seconds
    }, 3000);
  }

  /**
   * Click marker on map
   */
  onMarkerClick(driver: Driver, event: Event): void {
    this.showDriverPopup(driver, event.currentTarget as HTMLElement);
  }

  /**
   * Refresh driver locations
   */
  refreshLocations(): void {
    // this.locateDriversService.refreshDriverLocations()
    //   .pipe(takeUntil(this.destroy$))
    //   .subscribe(
    //     (drivers) => {
    //       this.drivers = drivers;
    //       this.updateCounts();
    //       this.applyFilters();
    //       alert('Driver locations refreshed!');
    //     },
    //     (error) => {
    //       console.error('Error refreshing locations:', error);
    //       alert('Failed to refresh locations');
    //     }
    //   );
  }

  /**
   * Get status badge class
   */
  getStatusClass(status: string): string {
    return status;
  }

  /**
   * Check if driver is selected
   */
  isDriverSelected(driverId: number): boolean {
    return this.selectedDriverId === driverId;
  }

  /**
   * Check if driver is visible (passes filters)
   */
  isDriverVisible(driver: Driver): boolean {
    return this.filteredDrivers.some(d => d.id === driver.id);
  }

  /**
   * Get marker position style
   */
  getMarkerPosition(driver: Driver): any {
    // Calculate position based on lat/lng or use default positions
    const positions: any = {
      1: { top: '35%', left: '42%' },
      2: { top: '25%', left: '58%' },
      3: { top: '48%', left: '68%' },
      4: { top: '65%', left: '72%' },
      5: { top: '55%', left: '28%' },
      6: { top: '72%', left: '35%' },
      7: { top: '78%', left: '55%' },
      8: { top: '38%', left: '82%' }
    };

    return positions[driver.id] || { top: '50%', left: '50%' };
  }

  /**
   * Get marker class
   */
  getMarkerClass(driver: Driver): string {
    return `driver-marker ${driver.status}`;
  }

  /**
   * Track by function for ngFor
   */
  trackByDriverId(index: number, driver: Driver): number {
    return driver.id;
  }
  @ViewChild(ReusableGoogleMapComponent) comp2!: ReusableGoogleMapComponent;
  openSendNotificationDialog(enterAnimationDuration: string,
    exitAnimationDuration: string) {
    const savedDrivers = JSON.parse(localStorage.getItem("locatedriversList") || "[]");
    const dialogRef = this.dialog.open(SendNotificationDialogComponent, {
      width: '90%', // Use a wider width for responsiveness
      maxWidth: '540px', // Adjusted to match SendNotification modal consistency
      enterAnimationDuration,
      exitAnimationDuration,
      panelClass: 'custom-modal-visible',
      data: {
        drivers: savedDrivers,  // pass driver list
        dropup: false
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log("Dialog data:", result);
        const formData = new FormData();
        const selected = result.driver;
        // If "all" → send empty string, else send the driver id
        if (selected == 'all') {
          formData.append('send_to_group', 'driver');
        }
        if (selected != 'all') {
          formData.append('send_to_user', selected);
        }
        this.locateDriverService.fetchDriverLocation(formData).subscribe((res: any) => {
          if (res?.resultCode == '0') {
            this.toast.show('error', res?.errorMessage);
          } else {
            this.toast.show('success', res?.resultDescription);
            this.comp2.onDriverSelected1(false);
            this.comp2.resetListener();
          }
        })
        // API to send notification
      }
    });
  }
}
